import { useState } from "react";

import { AutoAwesomeRounded, ExpandMoreRounded } from "@mui/icons-material";
import { Alert, Avatar, Box, Button, ButtonBase, Collapse, Stack, Typography } from "@mui/material";

import { InsightChart } from "./InsightChart";
import { Markdown } from "./Markdown";
import { ToolActivity } from "./ToolActivity";

function ThinkingSummary({ text, streaming }) {
	const [open, setOpen] = useState(false);
	if (!text) {
		return null;
	}
	const latest = text.trim().split("\n").filter(Boolean).pop();
	return (
		<Box sx={{ mb: 1 }}>
			<ButtonBase onClick={() => setOpen((v) => !v)} aria-expanded={open} sx={{ borderRadius: 1, px: 0.5, color: "text.secondary" }}>
				<Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", maxWidth: 640, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{streaming && !open ? latest : "Reasoning"}
				</Typography>
				<ExpandMoreRounded sx={{ fontSize: 18, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
			</ButtonBase>
			<Collapse in={open}>
				<Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "pre-wrap", borderLeft: 2, borderColor: "divider", pl: 1.5, mt: 0.5 }}>
					{text}
				</Typography>
			</Collapse>
		</Box>
	);
}

function TypingDots() {
	return (
		<Stack direction="row" spacing={0.5} sx={{ py: 1 }} aria-label="Working">
			{[0, 1, 2].map((i) => (
				<Box
					key={i}
					sx={{
						width: 6,
						height: 6,
						borderRadius: "50%",
						bgcolor: "text.disabled",
						animation: "insightsPulse 1.2s ease-in-out infinite",
						animationDelay: `${i * 0.15}s`,
						"@keyframes insightsPulse": { "0%, 80%, 100%": { opacity: 0.25 }, "40%": { opacity: 1 } },
					}}
				/>
			))}
		</Stack>
	);
}

export function ChatMessage({ message, onNewChat }) {
	if (message.role === "user") {
		return (
			<Stack direction="row" justifyContent="flex-end" sx={{ my: 2 }}>
				<Box sx={{ maxWidth: "75%", bgcolor: "primary.main", color: "primary.contrastText", px: 2, py: 1.25, borderRadius: "16px 16px 4px 16px" }}>
					<Typography variant="body2" sx={{ fontSize: 15, whiteSpace: "pre-wrap" }}>
						{message.text}
					</Typography>
				</Box>
			</Stack>
		);
	}

	const streaming = message.status === "streaming";
	const empty = message.parts.length === 0;
	return (
		<Stack direction="row" spacing={1.5} sx={{ my: 2 }} alignItems="flex-start">
			<Avatar sx={{ width: 30, height: 30, bgcolor: "primary.main", mt: 0.5 }}>
				<AutoAwesomeRounded sx={{ fontSize: 17 }} />
			</Avatar>
			<Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: 920 }}>
				<ThinkingSummary text={message.thinking} streaming={streaming && empty} />
				{message.parts.map((part) => {
					// A successful chart call is represented by the chart itself.
					if (part.type === "tool" && part.name === "render_chart" && part.status !== "error") {
						return null;
					}
					if (part.type === "text") {
						return <Markdown key={part.id}>{part.text}</Markdown>;
					}
					if (part.type === "tool") {
						return <ToolActivity key={part.id} part={part} />;
					}
					return <InsightChart key={part.id} spec={part.spec} />;
				})}
				{streaming && (empty || message.parts[message.parts.length - 1].type !== "text") && <TypingDots />}
				{message.status === "stopped" && (
					<Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", mt: 1 }}>
						Stopped.
					</Typography>
				)}
				{message.status === "error" && (
					<Alert
						severity="warning"
						sx={{ mt: 1 }}
						action={
							message.errorCode === "conversation_expired" ? (
								<Button color="inherit" size="small" onClick={onNewChat}>
									New chat
								</Button>
							) : undefined
						}
					>
						{message.error}
					</Alert>
				)}
			</Box>
		</Stack>
	);
}
